import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterLink } from '@angular/router';
import {
  EmptyStateComponent,
  PartnerService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  NgSharedModule,
} from '@principle-theorem/ng-shared';
import { Partner, PartnerApp } from '@principle-theorem/principle-core';
import { IPartnerApp } from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  HISTORY_DATE_FORMAT,
  snapshotDefined,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  EditPartnerAppDialogComponent,
  EditPartnerAppFormData,
  IEditPartnerAppRequest,
} from '../edit-partner-app-dialog/edit-partner-app-dialog.component';

@Component({
  selector: 'pr-app-list',
  imports: [
    CommonModule,
    MatListModule,
    MatCardModule,
    MatIconModule,
    EmptyStateComponent,
    NgSharedModule,
    RouterLink,
  ],
  templateUrl: './app-list.component.html',
  styleUrl: './app-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppListComponent {
  apps$: Observable<WithRef<IPartnerApp>[]>;
  readonly dateFormat = HISTORY_DATE_FORMAT;
  apiDocsUrl = environment.apiDocsUrl;

  constructor(
    private _basicDialog: BasicDialogService,
    private _partner: PartnerService,
    private _snackBar: MatSnackBar
  ) {
    this.apps$ = this._partner.partner$.pipe(
      filterUndefined(),
      switchMap((partner) => Partner.apps$(partner))
    );
  }

  async addNewApp(): Promise<void> {
    const app = await this._basicDialog
      .open<
        EditPartnerAppDialogComponent,
        IEditPartnerAppRequest,
        EditPartnerAppFormData
      >(
        EditPartnerAppDialogComponent,
        DialogPresets.medium({
          data: {
            title: 'Add Partner App',
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (!app) {
      return;
    }

    const partner = await snapshotDefined(this._partner.partner$);

    await Firestore.addDoc(Partner.appCol(partner), PartnerApp.init(app));
    this._snackBar.open('Partner App added');
  }

  async editApp(app: WithRef<IPartnerApp>): Promise<void> {
    const updatedApp = await this._basicDialog
      .open<
        EditPartnerAppDialogComponent,
        IEditPartnerAppRequest,
        EditPartnerAppFormData
      >(
        EditPartnerAppDialogComponent,
        DialogPresets.medium({
          data: {
            title: 'Edit Partner App',
            formData: {
              name: app.name,
            },
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (!updatedApp) {
      return;
    }

    await Firestore.patchDoc(app.ref, { name: updatedApp.name });
    this._snackBar.open('Partner App added');
  }
}
